import { useState } from 'react';
import { ChevronRight, Truck, PackagePlus } from 'lucide-react';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SearchableSelect } from './SearchableSelect';
import { useSalespersonsQuery } from '@/hooks/useSalespersons';
import { useAuthStore } from '@/stores/authStore';
import type { WarehouseCard, WarehouseOrderStatus } from '@/apis/warehouse';
import { useWarehouseHandlers, useUpdateAssignment, useUpdateWarehouseCardStatus } from '@/hooks/useWarehouse';
import {
  PACKAGE_TYPE_LABEL,
  PACKAGE_TYPE_STYLE,
  CLASSIFICATION_LABEL,
  CLASSIFICATION_STYLE,
  KANBAN_COLUMNS,
  WAREHOUSE_STATUS_STYLE,
  resolveClassification,
  canActOnOrder,
} from './warehouse-shared';
import { AddInboundTrackingDialog } from './AddInboundTrackingDialog';

interface PendingAssignment {
  field: 'salespersonId' | 'handlerId';
  roleLabel: 'Sales' | 'Handler';
  value: string;
  personLabel: string;
}

const SELECT_TRIGGER_CLASS = 'h-7 rounded-full border-smoke px-2.5 py-0 text-[10px] font-medium';

function formatCardDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

interface Props {
  card: WarehouseCard;
  status: WarehouseOrderStatus;
  onOpenDetails: (orderId: string) => void;
}

export function WarehouseOrderCard({ card, status, onOpenDetails }: Props) {
  const [logInboundOpen, setLogInboundOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<WarehouseOrderStatus | null>(null);
  const [pendingAssignment, setPendingAssignment] = useState<PendingAssignment | null>(null);
  const { data: handlers = [] } = useWarehouseHandlers();
  const { data: salespersons = [] } = useSalespersonsQuery();
  const updateAssignment = useUpdateAssignment(card.id);
  const updateStatus = useUpdateWarehouseCardStatus();
  const classification = resolveClassification(card.orderClassification, card.destinationCountry);
  const currentUser = useAuthStore((s) => s.user);
  const canAct = canActOnOrder(currentUser, card.assignedSalesperson?.id, card.assignedWarehouseHandler?.id);

  return (
    <div className="flex w-[216px] flex-col gap-3 rounded-xl border border-smoke bg-white p-3.5 drop-shadow-[0px_4px_4px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <p className="text-[13px] font-bold text-black">#{card.invoiceNumber || card.orderNumber}</p>
          <p className="text-[10px] font-normal text-lightGray">{formatCardDate(card.createdAt)}</p>
        </div>
        <div className="flex flex-wrap items-start gap-1">
          {card.packageType && (
            <span className={`rounded px-1.5 py-1 text-[10px] font-semibold ${PACKAGE_TYPE_STYLE[card.packageType]}`}>
              {PACKAGE_TYPE_LABEL[card.packageType]}
            </span>
          )}
          <span className={`rounded px-1.5 py-1 text-[10px] font-semibold ${CLASSIFICATION_STYLE[classification]}`}>
            {CLASSIFICATION_LABEL[classification]}
          </span>
          {card.destinationCountry && (
            <span className="flex items-center gap-1 rounded bg-smoke1 px-1 py-0.5 text-[10px] font-semibold text-black">
              {card.destinationCountry}
            </span>
          )}
        </div>
      </div>

      <div className="h-px w-full bg-smoke" />

      {/* Inbound tracking summary */}
      <div className="flex flex-col gap-1">
        {card.inboundTrackingSummary.length === 0 ? (
          <button
            type="button"
            onClick={() => setLogInboundOpen(true)}
            disabled={!canAct}
            className="flex items-center gap-1.5 text-xs font-medium text-primary-s500 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
          >
            <PackagePlus size={14} />
            Add Tracking
          </button>
        ) : (
          card.inboundTrackingSummary.map((entry) => (
            <div key={entry.id} className="flex items-center gap-1.5">
              <Truck size={14} className="shrink-0 text-lightGray" />
              <p className="truncate text-xs font-medium text-dark2">
                {entry.courierOrSupplierName}
                {entry.trackingNumber ? ` [${entry.trackingNumber}]` : ''}
              </p>
            </div>
          ))
        )}
      </div>

      <div className="h-px w-full bg-smoke" />

      {/* Sales + handler assignment — only the current owner (Sales/Handler, or Admin) may reassign */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <span className="w-12 shrink-0 text-[10px] font-normal text-dark3">Sales:</span>
          {canAct ? (
            <SearchableSelect
              value={card.assignedSalesperson?.id ?? ''}
              onValueChange={(v) => {
                const person = salespersons.find((sp) => sp.id === v);
                if (!person) return;
                setPendingAssignment({
                  field: 'salespersonId',
                  roleLabel: 'Sales',
                  value: v,
                  personLabel: `${person.firstName} ${person.lastName}`,
                });
              }}
              options={salespersons.map((sp) => ({ value: sp.id, label: `${sp.firstName} ${sp.lastName}` }))}
              triggerClassName={SELECT_TRIGGER_CLASS}
            />
          ) : (
            <span className="truncate text-[10px] font-medium text-black">
              {card.assignedSalesperson?.name || 'Unassigned'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-12 shrink-0 text-[10px] font-normal text-dark3">Handler:</span>
          {canAct ? (
            <SearchableSelect
              value={card.assignedWarehouseHandler?.id ?? ''}
              onValueChange={(v) => {
                const person = handlers.find((h) => h.id === v);
                if (!person) return;
                setPendingAssignment({
                  field: 'handlerId',
                  roleLabel: 'Handler',
                  value: v,
                  personLabel: `${person.firstName} ${person.lastName}`,
                });
              }}
              options={handlers.map((h) => ({ value: h.id, label: `${h.firstName} ${h.lastName}` }))}
              triggerClassName={SELECT_TRIGGER_CLASS}
            />
          ) : (
            <span className="truncate text-[10px] font-medium text-black">
              {card.assignedWarehouseHandler?.name || 'Unassigned'}
            </span>
          )}
        </div>
      </div>

      <div className="h-px w-full bg-smoke" />

      {/* Footer */}
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => onOpenDetails(card.id)}
          className="flex items-center gap-1 text-xs font-semibold capitalize text-primary-s500"
        >
          Order details
          <ChevronRight size={12} />
        </button>
        <Select
          value={status}
          onValueChange={(v) => setPendingStatus(v as WarehouseOrderStatus)}
        >
          <SelectTrigger className={SELECT_TRIGGER_CLASS}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {KANBAN_COLUMNS.map((s) => (
              <SelectItem key={s} value={s}>
                {WAREHOUSE_STATUS_STYLE[s].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <AddInboundTrackingDialog
        open={logInboundOpen}
        onOpenChange={setLogInboundOpen}
        orderId={card.id}
        assignedSalespersonId={card.assignedSalesperson?.id}
        assignedWarehouseHandlerId={card.assignedWarehouseHandler?.id}
      />

      <Dialog open={!!pendingStatus} onOpenChange={(v) => { if (!v) setPendingStatus(null); }}>
        <DialogContent className="w-100">
          <DialogHeader className="border-b border-smoke pb-3">
            <DialogTitle>Change Order Status?</DialogTitle>
          </DialogHeader>
          <p className="py-3 text-sm font-normal text-lightGray">
            Move order <span className="font-medium text-black">#{card.orderNumber}</span> from{' '}
            <span className="font-medium text-black">{WAREHOUSE_STATUS_STYLE[status].label}</span> to{' '}
            <span className="font-medium text-black">{pendingStatus && WAREHOUSE_STATUS_STYLE[pendingStatus].label}</span>?
          </p>
          <div className="mt-2 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setPendingStatus(null)}
              className="rounded-full border border-smoke px-5 py-2 text-sm text-black hover:bg-smoke1"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={updateStatus.isPending}
              onClick={() => {
                if (!pendingStatus) return;
                updateStatus.mutate(
                  { orderId: card.id, warehouseStatus: pendingStatus },
                  { onSettled: () => setPendingStatus(null) }
                );
              }}
              className="rounded-full bg-primary-s500 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {updateStatus.isPending ? 'Updating…' : 'Confirm'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingAssignment} onOpenChange={(v) => { if (!v) setPendingAssignment(null); }}>
        <DialogContent className="w-100">
          <DialogHeader className="border-b border-smoke pb-3">
            <DialogTitle>Reassign Order?</DialogTitle>
          </DialogHeader>
          <p className="py-3 text-sm font-normal text-lightGray">
            Set the <span className="font-medium text-black">{pendingAssignment?.roleLabel}</span> on order{' '}
            <span className="font-medium text-black">#{card.orderNumber}</span> to{' '}
            <span className="font-medium text-black">{pendingAssignment?.personLabel}</span>? The previous{' '}
            {pendingAssignment?.roleLabel.toLowerCase()} will no longer be able to act on this order.
          </p>
          <div className="mt-2 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setPendingAssignment(null)}
              className="rounded-full border border-smoke px-5 py-2 text-sm text-black hover:bg-smoke1"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={updateAssignment.isPending}
              onClick={() => {
                if (!pendingAssignment) return;
                updateAssignment.mutate(
                  { [pendingAssignment.field]: pendingAssignment.value },
                  { onSettled: () => setPendingAssignment(null) }
                );
              }}
              className="rounded-full bg-primary-s500 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {updateAssignment.isPending ? 'Reassigning…' : 'Confirm'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
